import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useLedgerEntries } from '@/hooks/react-query/useGeneralLedger';
import type { GeneralLedger } from '@/types/drawdown';
import { formatCurrency, formatDate } from '@/utils/formatters';

/** How many of the account's most recent lines the statement asks for. */
const PAGE_SIZE = 100;

/**
 * Reference type of a period-end retranslation line.
 *
 * Such a line moves no currency, only what the currency is carried at, so it
 * reads differently from the conversions around it.
 */
const RETRANSLATION_REFERENCE_TYPE = 'fx_retranslation';

interface CurrencyAccountStatementProps {
  fundId: string;
  accountCode: string;
  currency: string;
  fundCurrency: string;
  /** Today's holding in `currency`, which the running balance walks back from. */
  closingBalance: string;
  /** What that holding is carried at today, which the running value walks back from. */
  closingValue: string;
  /**
   * The date `closingBalance` was measured at, which the entries are cut off at
   * too. Both sides have to cover the same set or the running balance is wrong
   * from its first row: a conversion may be dated forward, and such a line is
   * absent from the position while still appearing in the list.
   */
  asOf: string;
}

/**
 * Every ledger line that touched one currency account, newest first.
 *
 * Both running columns are anchored on what the position query reports and
 * walked backwards, rather than accumulated from the oldest line up. The
 * entries are paged, so accumulating forwards would report figures short by
 * everything that did not fit on the page.
 *
 * Walking back only works while the anchors and the lines describe the same
 * set, which is what `asOf` holds them to: the position is measured to a date,
 * and a line dated after it would otherwise be subtracted from a balance that
 * never included it, throwing off every row beneath it as well.
 *
 * The two columns move independently, and a retranslation is the case that
 * shows why the statement carries both: the balance stays where it was while
 * the value moves underneath it.
 */
function withRunningTotals(
  entries: GeneralLedger[],
  closingBalance: string,
  closingValue: string
) {
  let balance = Number(closingBalance);
  let value = Number(closingValue);
  return entries.map((entry) => {
    const balanceAfter = balance;
    const valueAfter = value;
    balance -= Number(entry.fx_amount ?? 0);
    value -= entry.debit_amount - entry.credit_amount;
    return { entry, balanceAfter, valueAfter };
  });
}

export function CurrencyAccountStatement({
  fundId,
  accountCode,
  currency,
  fundCurrency,
  closingBalance,
  closingValue,
  asOf,
}: CurrencyAccountStatementProps) {
  const ledger = useLedgerEntries({
    fund_id: fundId,
    account_code: accountCode,
    date_to: asOf,
    page_size: PAGE_SIZE,
  });

  if (ledger.isLoading) {
    return (
      <p className="text-sm text-muted-foreground py-3">Loading entries…</p>
    );
  }

  // Before the empty case, which a failed fetch is otherwise indistinguishable
  // from: no data reads as no entries, and the row above says the account holds
  // a balance.
  if (ledger.isError) {
    return (
      <p className="text-sm text-destructive py-3">
        This account's entries could not be loaded. Reload before reading the
        balance above as unexplained.
      </p>
    );
  }

  const entries = ledger.data?.entries ?? [];
  if (entries.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-3">
        Nothing has touched this account yet.
      </p>
    );
  }

  const totalCount = ledger.data?.total_count ?? entries.length;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="text-right">{currency}</TableHead>
            <TableHead className="text-right">
              {fundCurrency.toUpperCase()}
            </TableHead>
            <TableHead className="text-right">Balance ({currency})</TableHead>
            <TableHead className="text-right">
              Value ({fundCurrency.toUpperCase()})
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {withRunningTotals(entries, closingBalance, closingValue).map(
            ({ entry, balanceAfter, valueAfter }) => {
              const isRetranslation =
                entry.reference_type === RETRANSLATION_REFERENCE_TYPE;
              return (
                <TableRow key={entry.id}>
                  <TableCell className="whitespace-nowrap">
                    {formatDate(entry.document_date)}
                  </TableCell>
                  <TableCell
                    className={isRetranslation ? 'text-muted-foreground' : ''}
                  >
                    {entry.description}
                  </TableCell>
                  {/* A retranslation moves no currency, so an em dash says that
                      outright rather than leaving a zero to be read as one. */}
                  <TableCell className="text-right">
                    {isRetranslation ? (
                      <span className="text-muted-foreground">-</span>
                    ) : (
                      formatCurrency(entry.fx_amount ?? 0, { currency })
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(entry.debit_amount - entry.credit_amount, {
                      currency: fundCurrency,
                    })}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(balanceAfter, { currency })}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(valueAfter, { currency: fundCurrency })}
                  </TableCell>
                </TableRow>
              );
            }
          )}
        </TableBody>
      </Table>
      {totalCount > entries.length && (
        <p className="text-xs text-muted-foreground pt-2">
          Showing the {entries.length} most recent of {totalCount} entries.
        </p>
      )}
    </>
  );
}
